import {
  type IdentityVerification,
  StaffGetCampaignOverviewResponseVendorCommunicationPreference as VendorCommunicationPreference,
  VendorModel,
} from '@urbanx/agx.campaigns.client';
import {
  AgxBadge,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
  formatPhoneNumber,
  formatName,
} from '@urbanx/agx.ui-components2';
import CampaignOverviewInfoCard from './CampaignOverviewInfoCard';
import CampaignOverviewSection from './CampaignOverviewSection';
import ContactDetails from './ContactDetails';
import IdentityVerificationIndicator from './IdentityVerificationIndicator';

interface VendorContact {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
  identityVerification?: IdentityVerification;
}

interface VendorSummary {
  name: string;
  contacts: VendorContact[];
}

const getVendorSummaries = (vendor: VendorModel): VendorSummary[] => {
  if (vendor.individual) {
    return [
      {
        name: vendor.legalName ?? formatName(vendor.individual.name),
        contacts: [
          {
            email: vendor.individual.email,
            phone: formatPhoneNumber(vendor.individual.phoneNumber),
            identityVerification: vendor.individual.identityVerification,
          },
        ],
      },
    ];
  }

  if (vendor.company) {
    return [
      {
        name: vendor.legalName ?? vendor.company.companyName ?? 'Company vendor',
        contacts: (vendor.company.signatories ?? []).map((signatory, signatoryIndex) => ({
          name: formatName(signatory.name),
          role: `Signatory ${signatoryIndex + 1}`,
          email: signatory.email,
          phone: formatPhoneNumber(signatory.phoneNumber),
          identityVerification: signatory.identityVerification,
        })),
      },
    ];
  }

  if (vendor.powerOfAttorney) {
    const principalName = vendor.legalName ?? formatName(vendor.powerOfAttorney.principalName);

    return (vendor.powerOfAttorney.attorneys ?? []).map((attorney) => ({
      name: `${formatName(attorney.name)} (Attorney for ${principalName})`,
      contacts: [
        {
          email: attorney.email,
          phone: formatPhoneNumber(attorney.phoneNumber),
          identityVerification: attorney.identityVerification,
        },
      ],
    }));
  }

  if (vendor.deceasedEstate) {
    return [
      {
        name: vendor.legalName ?? formatName(vendor.deceasedEstate.name),
        contacts: (vendor.deceasedEstate.executors ?? []).map((executor, executorIndex) => ({
          name: formatName(executor.name),
          role: `Executor ${executorIndex + 1}`,
          email: executor.email,
          phone: formatPhoneNumber(executor.phoneNumber),
          identityVerification: executor.identityVerification,
        })),
      },
    ];
  }

  return [{ name: vendor.legalName ?? 'Vendor', contacts: [] }];
};

interface VendorOverviewProps {
  vendors?: VendorModel[];
  communicationPreference?: VendorCommunicationPreference;
}

const VendorOverview = ({ vendors, communicationPreference }: VendorOverviewProps) => {
  const vendorSummaries =
    vendors?.flatMap((vendor, vendorIndex) =>
      getVendorSummaries(vendor).map((summary, summaryIndex) => ({
        key: `${vendor.id ?? vendorIndex}-${summaryIndex}`,
        summary,
      }))
    ) ?? [];

  return (
    <CampaignOverviewSection
      id="vendors"
      label="Vendors"
      badge={
        communicationPreference === VendorCommunicationPreference.Separately && (
          <AgxBadge type="colourfill" colour="red" size="sm" text="Contact separately" />
        )
      }
    >
      {vendorSummaries.length ? (
        vendorSummaries.map(({ key, summary }, vendorIndex) => {
          const directContact =
            summary.contacts.length === 1 && !summary.contacts[0].name
              ? summary.contacts[0]
              : undefined;

          if (directContact) {
            return (
              <CampaignOverviewInfoCard key={key}>
                <ContactDetails
                  id={`vendor-${vendorIndex}`}
                  name={summary.name}
                  email={directContact.email}
                  phone={directContact.phone}
                  badge={
                    <IdentityVerificationIndicator
                      id={`vendor-${vendorIndex}-identity-verification`}
                      identityVerification={directContact.identityVerification}
                    />
                  }
                />
              </CampaignOverviewInfoCard>
            );
          }

          return (
            <CampaignOverviewInfoCard key={key}>
              <AgxTypography id={`vendor-${vendorIndex}-name`} type="body_ui_strong" size="medium">
                {summary.name}
              </AgxTypography>
              {summary.contacts.map((contact, contactIndex) => (
                <AgxSpacer gap="spacing-xl" key={`${contact.name ?? 'contact'}-${contactIndex}`}>
                  <AgxDivider variant="horizontal" />
                  <ContactDetails
                    id={`vendor-${vendorIndex}-contact-${contactIndex}`}
                    name={contact.name}
                    role={contact.role}
                    email={contact.email}
                    phone={contact.phone}
                    compact
                    badge={
                      <IdentityVerificationIndicator
                        id={`vendor-${vendorIndex}-contact-${contactIndex}-identity-verification`}
                        identityVerification={contact.identityVerification}
                      />
                    }
                  />
                </AgxSpacer>
              ))}
            </CampaignOverviewInfoCard>
          );
        })
      ) : (
        <CampaignOverviewInfoCard>
          <ContactDetails id="vendors-empty" emptyMessage="No vendor details recorded" />
        </CampaignOverviewInfoCard>
      )}
    </CampaignOverviewSection>
  );
};

export default VendorOverview;
